import {useMutation, useQueryClient} from '@tanstack/react-query';
import CustomerPortalAPI, {DeepThoughtError, Policy} from '../../../APIs/CustomerPortalAPI';

export type PolicyMutationInput = Partial<Omit<Policy, 'documents'>> & {
    documentIds?: string[];
};

async function createPolicyForCustomer(...args: Parameters<typeof CustomerPortalAPI.createPolicyForCustomer>) {
    const resp = await CustomerPortalAPI.createPolicyForCustomer(...args);
    if (resp.errors || !resp.data?.createPolicy) throw new DeepThoughtError(resp.errors);
    return resp.data.createPolicy;
}

async function updatePolicyForCustomer(...args: Parameters<typeof CustomerPortalAPI.updatePolicyForCustomer>) {
    const resp = await CustomerPortalAPI.updatePolicyForCustomer(...args);
    if (resp?.errors || !resp?.data?.updatePolicy) throw new DeepThoughtError(resp?.errors);
    return resp?.data.updatePolicy;
}

export function usePolicyMutation() {
    const queryClient = useQueryClient();

    return useMutation<Policy, Error, PolicyMutationInput, {previousPolicies: Policy[]}>(
        async (input) => {
            /* prettier-ignore */
            const mutate = input.id
                ? updatePolicyForCustomer
                : createPolicyForCustomer;
            return mutate(input);
        },
        {
            onMutate: async (input) => {
                const queryKey = ['policies', {customerId: input.customerId}];
                await queryClient.cancelQueries({queryKey});
                const previousPolicies: Policy[] = queryClient.getQueryData(queryKey) || [];
                queryClient.setQueriesData({queryKey}, (old: Policy[] = []) => {
                    if (input.id) {
                        const oldIndex = old.findIndex((p) => p.id === input.id);
                        if (oldIndex !== -1) {
                            const oldValue = old[oldIndex];
                            return [...old.slice(0, oldIndex), {...oldValue, ...input}, ...old.slice(oldIndex + 1)];
                        }
                    }
                    return [...old, input as Policy];
                });

                return {previousPolicies};
            },
            onError: (err, input, context) => {
                if (context?.previousPolicies) {
                    queryClient.setQueryData(['policies', {customerId: input.customerId}], context.previousPolicies);
                }
            },
            onSettled: (data, err, input) => {
                queryClient.invalidateQueries({queryKey: ['policies', {customerId: input.customerId}]});
            },
        },
    );
}

export default usePolicyMutation;
